import { apiUrl } from '../utils/api-url';

/**
 * Client for the questionnaire engine (orchestrator RFC §4.4). The wizard panel
 * is a thin client of QuestionnaireService — every answer round-trips through
 * the server-side validate→store→write-back pipeline; no progression logic
 * lives in the browser.
 */

export interface QuestionOption {
  value: string;
  label: string;
}

export interface QuestionDependsOn {
  questionId: string;
  equals: string;
}

export interface QuestionMapsTo {
  target: string;
  path: string;
}

export interface QuestionnaireQuestion {
  id: string;
  prompt: string;
  /** text | number | boolean | choice */
  type: string;
  required: boolean;
  hint?: string;
  options?: QuestionOption[];
  min?: number;
  max?: number;
  dependsOn?: QuestionDependsOn;
  mapsTo?: QuestionMapsTo;
  prefillFrom?: string;
  deviationHints?: string[];
}

export interface QuestionView {
  question: QuestionnaireQuestion;
  answerValue?: string | null;
  answerSource?: string | null;
  answeredAt?: string | null;
  visible: boolean;
  /** Pre-fill suggestion — never auto-saved; the user applies it explicitly. */
  prefill?: string | null;
}

export interface SectionView {
  id: string;
  title: string;
  questions: QuestionView[];
}

export interface QuestionnaireView {
  id: string;
  title: string;
  version: string;
  description?: string | null;
  /** in-progress | complete */
  status: string;
  currentSectionId?: string | null;
  answered: number;
  visibleTotal: number;
  nextQuestionId?: string | null;
  progressLine: string;
  sections: SectionView[];
}

export interface NextQuestionInfo {
  id: string;
  prompt: string;
  sectionId: string;
  type: string;
  options?: QuestionOption[] | null;
}

export interface AnswerResult {
  saved: boolean;
  errors: string[];
  warnings: string[];
  status: string;
  answered: number;
  visibleTotal: number;
  next?: NextQuestionInfo | null;
  progressLine: string;
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json() as { error?: string };
    if (body.error) return body.error;
  } catch { /* non-JSON error body — keep the status message */ }
  return fallback;
}

/** Merged definition + state + visibility + pre-fill suggestions. */
export async function getQuestionnaire(
  projectId: string,
  questionnaireId: string,
  customerId: string,
  token: string,
): Promise<QuestionnaireView> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/questionnaires/${encodeURIComponent(questionnaireId)}?customerId=${encodeURIComponent(customerId)}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(await readError(res, `Failed to load questionnaire: ${res.status}`));
  return res.json();
}

/**
 * Submit one answer. Server-side validation — returns structured errors plus
 * write-back / architect-override warnings. An empty value clears an optional answer.
 */
export async function submitAnswer(
  projectId: string,
  questionnaireId: string,
  customerId: string,
  questionId: string,
  value: string,
  token: string,
): Promise<AnswerResult> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/questionnaires/${encodeURIComponent(questionnaireId)}/answers?customerId=${encodeURIComponent(customerId)}`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ questionId, value }),
    },
  );
  if (!res.ok) throw new Error(await readError(res, `Failed to save answer: ${res.status}`));
  return res.json();
}

/** Mark complete — rejected with structured blockers while required visible questions are unanswered. */
export async function completeQuestionnaire(
  projectId: string,
  questionnaireId: string,
  customerId: string,
  token: string,
): Promise<AnswerResult> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/questionnaires/${encodeURIComponent(questionnaireId)}/complete?customerId=${encodeURIComponent(customerId)}`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!res.ok) throw new Error(await readError(res, `Failed to complete questionnaire: ${res.status}`));
  return res.json();
}
